/**
 * CAI DAT HE THONG MAC DINH — bang `settings` (key/value jsonb), docs/03 §3.2.
 * Nhom khoa: security.* | sync.* | policy.* | smtp.* | branding.* | backup.*  (docs/04 §4.11)
 * Gia tri goi y lay tu docs/02 §2.9 (FR-110..116) va docs/07.
 */

export const SETTING_GROUPS = ['security', 'sync', 'policy', 'smtp', 'branding', 'backup'] as const;
export type SettingGroup = (typeof SETTING_GROUPS)[number];

/** Khoa co gia tri bi mat — API tra ve "***" va khong bao gio ghi vao audit (docs/07 §7.6). */
export const SECRET_SETTING_KEYS = [
  'smtp.password',
  'smtp.resend_api_key',
  /*
   * OQ-273 → OQ-294 — khoá ứng dụng OAuth cho kho sao lưu ngoài.
   *
   * `client_secret` mở đường vào TOÀN BỘ Dropbox / Google Drive của người cấp
   * quyền, không riêng thư mục sao lưu. Nó phải được mã hoá khi lưu và che khi
   * đọc ra, đúng như `smtp.password` — nếu không, ai có `adm.settings` đọc
   * được là cầm luôn chìa khoá kho lưu trữ cá nhân.
   *
   * `refresh_token` không còn ở đây: từ OQ-294 mỗi kho là một hàng trong
   * `backup_remotes`, và token nằm ở cột `refresh_token` của hàng ấy — mã hoá
   * bằng cùng cơ chế, và **không bao giờ** trả ra API.
   */
  'backup.oauth.dropbox.client_secret',
  'backup.oauth.gdrive.client_secret',
  /*
   * OQ-316 — khoá bí mật Turnstile.
   *
   * Nó là thứ duy nhất chứng minh với Cloudflare rằng lượt kiểm captcha đến từ
   * hệ thống này. Lộ ra thì người khác dùng nó để tự xác thực token, và tấm
   * chắn chống đăng ký rác thành hình vẽ trên giấy.
   */
  'security.turnstile_secret_key',
] as const;

export const SETTING_MASK = '***';

/**
 * Gia tri dai dien MOI vai tro trong `security.force_2fa_roles`.
 *
 * Cong tac "Bat buoc 2FA cho moi tai khoan" cua `settings.html` ghi ky hieu nay
 * vao chinh danh sach vai tro, thay vi them mot khoa rieng — them khoa rieng se
 * keo theo sua chu ky `mustSetupTwoFactor()` va hai tep nen tang
 * (`core/middleware/authenticate.ts`, `modules/auth/auth.router.ts`).
 * An toan vi `slug()` khong bao gio sinh ra ky tu nay. Xem OQ-027.
 */
export const FORCE_2FA_ALL = '*';

export const DEFAULT_SETTINGS: Readonly<Record<string, unknown>> = {
  // ── Bao mat (docs/07 §7.1, §7.2, §7.3, §7.4; FR-110) ──────────────
  'security.min_password_length': 12,
  'security.require_uppercase': true,
  'security.require_lowercase': true,
  'security.require_number': true,
  'security.require_symbol': false,
  'security.password_history': 5,
  'security.password_expiry_days': 0, // 0 = khong bat buoc doi
  'security.check_pwned_passwords': true,
  'security.max_failed_logins': 5,
  'security.lockout_minutes': 15,
  'security.session_absolute_days': 30,
  'security.session_idle_days': 7,
  'security.allow_multiple_sessions': true,
  // Slug vai tro bat buoc bat 2FA; chua `FORCE_2FA_ALL` ('*') nghia la moi tai khoan.
  'security.force_2fa_roles': [],
  'security.admin_ip_allowlist': [],
  'security.invitation_expiry_days': 7,
  'security.password_reset_ttl_minutes': 60,
  'security.email_change_ttl_minutes': 60,
  'security.trusted_device_days': 30,

  // ── Dong bo (FR-111) — M3+ moi dung ───────────────────────────────
  'sync.zone_interval_minutes': 360,
  'sync.token_check_interval_minutes': 360,
  // docs/06 §6.5 bang bon job: chu ky doc tu `settings.sync.*`.
  'sync.dns_sync_interval_minutes': 360,
  'sync.verify_ns_interval_minutes': 30,
  'sync.max_retries': 5,
  'sync.backoff_base_ms': 1000,

  // ── Chinh sach (FR-112) ───────────────────────────────────────────
  'policy.require_admin_approval_for_domains': false,
  'policy.max_domains_per_user': 0, // 0 = khong gioi han
  'policy.max_connections_per_user': 0,
  'policy.allow_user_invites': true,
  /*
   * OQ-315 — cho nguoi la tu dang ky tai khoan.
   *
   * Mac dinh BAT vi chu du an yeu cau dung ngay. Nhung day la cong tac duy nhat
   * mo cua he thong ra Internet, nen no phai TAT duoc tu giao dien: neu bi dang
   * ky rac, nguoi van hanh can chan trong mot phut chu khong phai cho mot lan
   * deploy.
   */
  'policy.allow_public_signup': true,
  /*
   * OQ-316 — chống đăng ký rác. Chủ dự án: *"thêm số lượt đăng ký trên cùng 1 IP
   * trong khoảng thời gian nào đó. Thêm cả capcha của cloudflare luôn. Tất cả
   * cấu hình có thể chỉnh sửa trong cài đặt hệ thống"*.
   *
   * Hai lớp khác nhau chứ không phải hai bản của một lớp: giới hạn IP chặn máy
   * gửi rác dễ nhất và không cần cấu hình gì; captcha chặn cả những lượt đổi IP
   * nhưng đòi khoá từ Cloudflare. Bật một lớp vẫn có tác dụng.
   */
  'security.signup_rate_limit': 3,
  'security.signup_rate_window_minutes': 60,
  'security.turnstile_enabled': false,
  'security.turnstile_site_key': '',
  'security.turnstile_secret_key': '',

  /*
   * OQ-328 — tra vị trí gần đúng từ IP cho danh sách Phiên & thiết bị.
   *
   * Bật mặc định vì cột "Vị trí" đã có sẵn trên màn hình và trống thì vô dụng.
   * Nhưng phải tắt được: đây là lượt gửi địa chỉ IP của người đăng nhập sang một
   * dịch vụ bên thứ ba, và đó là quyết định của người vận hành, không phải của tôi.
   */
  'security.geoip_enabled': true,

  // ── SMTP (FR-113) ─────────────────────────────────────────────────
  'smtp.host': '',
  'smtp.port': 587,
  'smtp.secure': true,
  'smtp.user': '',
  'smtp.password': '',
  /*
   * Bỏ trống có chủ ý (OQ-281): khi trống, `mailer.ts` rơi về `MAIL_FROM`, mà
   * `MAIL_FROM` suy từ `APP_URL`. Nhờ vậy một hệ thống mới dựng đã gửi thư từ
   * đúng domain của nó, không cần ai đi điền.
   *
   * Mặc định cũ là `no-reply@dnshub.local` — một địa chỉ không ai sở hữu được
   * (`.local` là TLD dành riêng cho mDNS), nên thư hoặc bị từ chối, hoặc rơi
   * vào thư rác. Quản trị viên vẫn đổi được ở Cài đặt → Email khi cần domain
   * gửi thư khác domain ứng dụng.
   */
  'smtp.from_email': '',
  'smtp.from_name': 'DNSHub',
  /*
   * OQ-240 — chọn đường gửi thư: 'smtp' hoặc 'resend'.
   *
   * Resend gửi qua HTTPS nên không cần cổng, không cần TLS, không bị nhà mạng
   * chặn cổng 587 — dựng xong trong một phút bằng đúng một API key. SMTP vẫn là
   * mặc định vì nó chạy được với mọi nhà cung cấp; Resend là lối tắt cho người
   * cần thử ngay.
   *
   * Nhóm khoá vẫn là `smtp.*`: đổi tên nhóm sẽ phá `SETTING_GROUPS`, đường API
   * `PATCH /settings`, và mọi khoá đã lưu. Tên nhóm ở đây nghĩa là "gửi thư",
   * không phải "giao thức SMTP".
   */
  'smtp.provider': 'smtp',
  'smtp.resend_api_key': '',

  // ── Thuong hieu (FR-114) ──────────────────────────────────────────
  'branding.app_name': 'DNSHub',
  /*
   * Dòng phụ dưới tên hệ thống (OQ-290). Mặc định RỖNG chứ không phải một câu
   * cụ thể: rỗng nghĩa là "Admin chưa đặt", và giao diện rơi về bản dịch theo
   * ngôn ngữ đang chọn. Đặt sẵn một câu tiếng Việt ở đây sẽ khoá cứng nó cho
   * cả người dùng tiếng Anh.
   */
  'branding.tagline': '',
  'branding.logo_light': null,
  'branding.logo_dark': null,
  'branding.favicon': null,
  'branding.accent_color': null,
  'branding.footer_text': '',
  'branding.default_locale': 'vi',
  'branding.default_timezone': 'Asia/Ho_Chi_Minh',

  // ── Sao luu tu dong (docs/09 §9.4) ────────────────────────────────
  /*
   * docs/09 §9.4 doi "sao luu hang ngay". Truoc M7 chi co duong THU CONG
   * (OQ-136: Cai dat -> tab Sao luu), tuc lich sao luu la mot LOI HUA cua tai
   * lieu ma khong co ma nao thuc hien. Ba khoa duoi bien loi hua ay thanh mot
   * cai cong tac that.
   *
   * Lich nam trong TIEN TRINH API, khong phai `apps/worker` — da do: worker la
   * tien trinh RIENG, va mo cung thu muc PGlite tu tien trinh thu hai KHONG bi
   * chan ma lam TREO tien trinh dang giu. Xem `backup.schedule.ts`.
   */
  'backup.auto_enabled': true,
  'backup.interval_hours': 24, // docs/09 §9.4 — "hang ngay"
  'backup.keep_count': 7, // giu mot tuan; moi ban ~5 MB

  /*
   * ── Ung dung OAuth cho kho sao luu ngoai (OQ-294, thay OQ-273) ─────────
   *
   * Chu du an trien khai bang Docker tren Coolify. Ban sao luu nam trong
   * volume cua container: khoi phuc nhanh, nhung mat may la mat luon — va
   * "mat may" chinh la truong hop nguoi ta can ban sao luu nhat.
   *
   * OQ-273 chon "dan refresh token" vi luc ay CHUA CO domain cong khai va
   * callback URL. Nay da co, nen rao ay het — xem migration 0017.
   *
   * Bon khoa nay mo ta MOT UNG DUNG OAuth dung chung cho moi kho cung nha
   * cung cap, khong phai mot kho. Do la toan bo ly do them tai khoan thu hai
   * khong phai dang ky app lai: dang ky mot lan, ket noi bao nhieu tai khoan
   * cung duoc.
   *
   * Tung KHO nam o bang `backup_remotes` — mot kho la mot thuc the co vong
   * doi rieng (ket noi, kiem tra, hong, xoa), khong phai mot cap key/value.
   *
   * Hai `client_secret` nam trong SECRET_SETTING_KEYS: chung mo duong vao
   * TOAN BO Dropbox / Drive cua nguoi cap quyen, khong rieng thu muc sao luu.
   */
  'backup.oauth.dropbox.client_id': '',
  'backup.oauth.dropbox.client_secret': '',
  'backup.oauth.gdrive.client_id': '',
  'backup.oauth.gdrive.client_secret': '',
};

export const DEFAULT_SETTING_KEYS: readonly string[] = Object.keys(DEFAULT_SETTINGS);

export function settingGroupOf(key: string): string {
  const idx = key.indexOf('.');
  return idx === -1 ? key : key.slice(0, idx);
}

export function isSecretSettingKey(key: string): boolean {
  return (SECRET_SETTING_KEYS as readonly string[]).includes(key);
}
